import { productTitle, productDelivery } from '../utils/productIdentity';
import { useState, useEffect, useRef } from 'preact/hooks';
import { getConfig } from '../config';
import { getCart } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { formatPrice } from '../utils/currency';
import { t } from '../i18n';

interface MiniCartProduct {
  productid?: string;
  product_title?: string;
  made_in?: string;
  weight_value?: string;
  set_value?: string;
  amount?: number;
  product_price?: string;
  [key: string]: unknown;
}

interface MiniCartData {
  products?: MiniCartProduct[];
  bonus?: MiniCartProduct & { product_title?: string; weight_value?: string; set_value?: string };
  subtotal?: string | number;
  total?: string | number;
  discount?: number;
  discount_type?: string;
  discount_value?: string | number;
  bonus_discount?: string | number;
  coupon_discount?: string | number;
  coupon_type?: string;
  coupon_value?: string | number;
  shipping_cost?: number;
  shipping_method?: string;
  [key: string]: unknown;
}

const SHIPPING_INSURANCE = 7;

function translateShippingMethod(method: unknown): string {
  const value = String(method ?? '').trim();
  if (!value) return '';
  const key = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return t(`shippingMethod.${key}`, value);
}

export function CheckoutMiniCart() {
  const config = getConfig();
  const [cart, setCart] = useState<MiniCartData | null>(null);
  const prevCartKey = useRef<string>('');

  const loadCart = () => {
    getCart(config).then((res) => {
      if (res.status === 200 && res.result) setCart(res.result as MiniCartData);
    });
  };

  useEffect(() => {
    loadCart();
    return store.subscribe(() => {
      const key = `${store.getCartCount()}-${store.getCartTotal()}`;
      if (key === prevCartKey.current) return;
      prevCartKey.current = key;
      loadCart();
    });
  }, []);

  const openCart = (e: Event) => {
    e.preventDefault();
    navigateToView('cart');
  };

  if (!cart?.products?.length) return null;

  const shippingCost = Number(cart.shipping_cost) || 0;
  const shippingDisplay = shippingCost > 0 ? shippingCost - SHIPPING_INSURANCE : 0;
  const hasPersonalDiscount = Number(cart.discount) > 0 && Number(cart.discount_value) > 0;
  const hasBonusDiscount = Number(cart.bonus_discount) > 0;
  const hasCouponDiscount = Number(cart.coupon_discount) > 0 && cart.coupon_type !== 'free_ship';
  const hasFreeShipCoupon = Number(cart.coupon_discount) > 0 && cart.coupon_type === 'free_ship';

  return (
    <div class="card mb-4 border shadow-sm overflow-hidden">
      {/* Products table */}
      <div class="px-3 pt-3 table-responsive">
        <table class="table table-borderless align-middle mb-0" style={{ '--bs-table-bg': 'transparent' } as any}>
          <thead>
            <tr class="border-bottom" style={{ fontSize: 'var(--sw-font-size-sm)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--sw-muted)' }}>
              <th class="fw-normal pb-2 ps-0">{t('cartPage.product')}</th>
              <th class="fw-normal pb-2 text-center">{t('cartPage.qty')}</th>
              <th class="fw-normal pb-2 text-end sw-col-unit-price">{t('cartPage.price')}</th>
              <th class="fw-normal pb-2 text-end pe-0">{t('cartPage.lineTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {cart.products.map((p) => (
              <tr key={p.productid} class="border-bottom">
                <td class="ps-0 py-2">
                  <div class="fw-semibold" style={{ lineHeight: 1.3 }}>
                    {productTitle(p)}
                    {p.made_in && <span class="fw-normal text-muted"> · {t('product.madeIn', 'Made in')}: {p.made_in}</span>}
                    <div class="small text-muted sw-product-delivery">{t('product.delivery', 'Delivery')}: {productDelivery(p)}</div>
                  </div>
                  {(p.weight_value || p.set_value) && (
                    <div class="small text-muted mt-1">
                      {[p.weight_value, p.set_value].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td class="text-center py-2">
                  <span class="badge rounded-pill" style={{ background: 'var(--sw-border)', color: 'var(--sw-text)', fontWeight: 600, fontSize: 'var(--sw-font-size-sm)', padding: '0.3em 0.7em' }}>
                    {p.amount}
                  </span>
                </td>
                <td class="text-end py-2 text-muted small text-nowrap sw-col-unit-price">{formatPrice(p.product_price)}</td>
                <td class="text-end py-2 fw-semibold text-nowrap pe-0">
                  {formatPrice(Number(p.product_price) * Number(p.amount))}
                </td>
              </tr>
            ))}
            {cart.bonus && (
              <tr class="border-bottom">
                <td class="ps-0 py-2">
                  <div class="fw-semibold text-success">{t('miniCart.bonus')} {productTitle(cart.bonus)}</div>
                  {(cart.bonus.weight_value || cart.bonus.set_value) && (
                    <div class="small text-muted mt-1">
                      {[cart.bonus.weight_value, cart.bonus.set_value].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td class="text-center py-2">
                  <span class="badge rounded-pill" style={{ background: 'var(--sw-border)', color: 'var(--sw-text)', fontWeight: 600, fontSize: 'var(--sw-font-size-sm)', padding: '0.3em 0.7em' }}>1</span>
                </td>
                <td class="text-end py-2 small text-muted sw-col-unit-price">—</td>
                <td class="text-end py-2 fw-semibold text-success text-nowrap pe-0">{t('miniCart.free')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div class="px-3 pb-3 pt-2 d-flex flex-column align-items-end">
        <table class="table table-sm table-borderless mb-0" style={{ width: 'auto', minWidth: '240px', '--bs-table-bg': 'transparent' } as any}>
          <tbody>
            {cart.shipping_method && (
              <tr>
                <td class="text-muted pe-4 py-1">{t('miniCart.deliveryMethod', 'Delivery method')}</td>
                <td class="text-end fw-medium py-1 text-nowrap">{translateShippingMethod(cart.shipping_method)}</td>
              </tr>
            )}
            <tr>
              <td class="text-muted pe-4 py-1">{t('miniCart.subtotal')}</td>
              <td class="text-end fw-medium py-1 text-nowrap">{formatPrice(cart.subtotal)}</td>
            </tr>
            {hasPersonalDiscount && (
              <tr>
                <td class="text-success pe-4 py-1">{t('miniCart.personalDiscount')}</td>
                <td class="text-end text-success py-1 text-nowrap">
                  {cart.discount_type === 'absolute' ? '$' : ''}{cart.discount}{cart.discount_type === 'percent' ? '%' : ''} − {formatPrice(cart.discount_value)}
                </td>
              </tr>
            )}
            {hasBonusDiscount && (
              <tr>
                <td class="text-success pe-4 py-1">{t('miniCart.bonusDiscount')}</td>
                <td class="text-end text-success py-1 text-nowrap">− {formatPrice(cart.bonus_discount)}</td>
              </tr>
            )}
            {hasCouponDiscount && (
              <tr>
                <td class="text-success pe-4 py-1">{t('miniCart.discountCoupon')}</td>
                <td class="text-end text-success py-1 text-nowrap">
                  {cart.coupon_type === 'percent' && cart.coupon_value ? `${Math.round(Number(cart.coupon_value))}% − ` : ''}
                  {formatPrice(cart.coupon_discount)}
                </td>
              </tr>
            )}
            <tr>
              <td class="text-muted pe-4 py-1">{t('miniCart.shippingCost')}</td>
              <td class="text-end fw-medium py-1 text-nowrap">
                {hasFreeShipCoupon || shippingCost <= 0 ? t('miniCart.freeShipping') : formatPrice(shippingDisplay)}
              </td>
            </tr>
            {shippingCost > 0 && (
              <tr>
                <td class="text-muted pe-4 py-1">{t('miniCart.shippingInsurance')}</td>
                <td class="text-end fw-medium py-1 text-nowrap">{formatPrice(SHIPPING_INSURANCE)}</td>
              </tr>
            )}
            <tr class="border-top">
              <td class="pe-4 pt-2 pb-1 fw-bold">{t('miniCart.total')}</td>
              <td class="text-end pt-2 pb-1 fw-bold text-nowrap sw-ui-total" style={{ color: 'var(--sw-primary)' }}>{formatPrice(cart.total)}</td>
            </tr>
          </tbody>
        </table>

        <a
          href="#"
          onClick={openCart}
          class="btn btn-sm btn-outline-secondary mt-2 sw-mini-cart-edit"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"/>
          </svg>
          <span>{t('miniCart.editCart')}</span>
        </a>
      </div>
    </div>
  );
}
